import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import type { CarrierCodeFilters, CarrierCodes } from '../models';
import * as carrierCodesService from '../services/carrier-codes.service';

export const useCarrierCodes = (
  defaultFilters: Partial<CarrierCodeFilters & { searchParams: { ids: string[] } }> = {},
  { enabled = true }: { enabled?: boolean } = {},
) => {
  const { searchParams, ...otherDefaultFilters } = useMemo(() => defaultFilters, [defaultFilters]);
  const [filters, setFilters] = useState<Partial<CarrierCodeFilters>>(otherDefaultFilters);
  const isEnabled = enabled && Boolean(filters.carrierKey);

  const cacheData = carrierCodesService.getAll();

  const updateFilters = useCallback((newFilters: Partial<CarrierCodeFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: only react to identity filter changes
  useEffect(() => {
    updateFilters(otherDefaultFilters);
  }, [otherDefaultFilters.carrierKey, updateFilters]);

  const { data, isLoading, isError } = useQuery(
    carrierCodesService.carrierCodesQueryOptions(filters, { enabled: isEnabled }),
  );

  const missingIds = useMemo(() => {
    if (!searchParams?.ids?.length || !data?.map) {
      return [];
    }
    return searchParams.ids.filter((id) => !data?.map[id] && !cacheData?.map[id]);
  }, [searchParams?.ids, data?.map, cacheData?.map]);

  const {
    data: preloadData,
    isLoading: isPreloadLoading,
    isError: isPreloadError,
  } = useQuery(
    carrierCodesService.carrierCodesQueryOptions(
      { search: { id: missingIds.join(',') } },
      { enabled: isEnabled && missingIds.length > 0 },
    ),
  );

  const mergedMap: CarrierCodes['map'] = {
    ...(data?.map ?? {}),
    ...(cacheData?.map ?? {}),
    ...(preloadData?.map ?? {}),
  };

  return {
    data: {
      list: isEnabled ? Object.values(mergedMap) : [],
      map: mergedMap,
    },
    isError: isError || isPreloadError,
    isLoading: isLoading || isPreloadLoading,
    updateFilters,
  };
};
